import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { COMPANY_NAME, COMPANY_RIF, companyPageTitle } from "../utils/company.js";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getInvoices,
  getClientList,
  getProducts,
  getPaymentMethods,
  getBcvRate,
  getNextInvoiceReference,
  deleteInvoicePayment,
  deleteInvoice,
} from "../utils/api.js";
import { Card, Button } from "../components/legacy-ui.js";
import { AppPageHeader, AppModalShell, AppModalFooter, AppTable } from "../components/app-ui.js";
import { formatCurrency, formatDate, sellerLabel } from "../utils/formatters.js";
import {
  invoiceHasPendingBalance,
  resolvePaymentMethodName,
} from "../utils/paymentUtils.js";
import {
  Plus,
  Search,
  Download,
  LayoutGrid,
  List,
} from "lucide-react";
import InvoiceViewModal from "../components/InvoiceViewModal.js";
import { useAuth } from "../context/AuthContext";
import InvoiceForm from "./invoices/InvoiceForm";
import PaymentForm from "./invoices/PaymentForm";
import { LoadingState } from "../components/motion.js";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const INVOICES_VIEW_STORAGE_KEY = "lactium:invoices-view";

const readInvoicesView = () => {
  try {
    const stored = localStorage.getItem(INVOICES_VIEW_STORAGE_KEY);
    return stored === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const InvoicesModule = ({ initialData, setInitialData }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = !user?.role || user.role === "admin";
  const canManageInvoices = isAdmin || Boolean(user?.permissions?.manageInvoices);
  const canManagePayments = isAdmin || Boolean(user?.permissions?.managePayments);
  const [prefillData, setPrefillData] = useState(null);
  const formInitialData = initialData || prefillData;
  const formSetInitialData = setInitialData || setPrefillData;
  // Data state
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bcvRate, setBcvRate] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [staticDataLoading, setStaticDataLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [productSummary, setProductSummary] = useState([]);
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const [newInvoiceError, setNewInvoiceError] = useState("");
  const [nextInvoiceReference, setNextInvoiceReference] = useState(null);
  const [recentInvoiceNumbers, setRecentInvoiceNumbers] = useState([]);
  const totalProductSummaryAmount = productSummary.reduce(
    (acc, product) => acc + (product.totalAmount || 0),
    0,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProduct, setFilterProduct] = useState("");
  const [viewMode, setViewMode] = useState(() => readInvoicesView());
  const searchingInvoices = Boolean(debouncedSearchTerm.trim());
  const sortedProducts = [...products].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "es", {
      sensitivity: "base",
    }),
  );

  const setInvoicesView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(INVOICES_VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  };
  const hasMaturationSummary =
    isAdmin && !searchingInvoices && productSummary.some((product) => product.hasMaturation);
  const formatNumberVe = (value) =>
    new Intl.NumberFormat("es-VE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  const formatCount = (value) =>
    new Intl.NumberFormat("es-VE", {
      maximumFractionDigits: 0,
    }).format(value || 0);
  const formatRemaining = (product) => {
    if (!product?.hasMaturation) return "—";
    const kg = formatNumberVe(product.remainingKg);
    if (!(product.cheeseCount > 0)) return `${kg} kg`;
    return `${formatCount(product.remainingCount)} · ${kg} kg`;
  };
  const formatRemainingUnit = (product) => {
    if (!product?.hasMaturation) return "—";
    if (product.cheeseCount > 0) return formatCount(product.remainingCount);
    return formatNumberVe(product.remainingKg);
  };

  // Week navigation by month-week index (week 1 = days 1-7, week 2 = 8-14, etc.)
  // We'll keep a `currentMonth` (YYYY-MM) and `weekIndex` (1-based) and compute start/end for the API.
  const today = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Caracas" }));
  const pad = (n) => String(n).padStart(2, "0");
  const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; // YYYY-MM

  const getWeeksInMonth = useCallback((year, month) => {
    const weeks = [];
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Lunes es 0 en este offset, Domingo es 6
    
    let currentMonday = new Date(year, month, 1 - offset);
    
    while (true) {
      const currentSunday = new Date(currentMonday.getFullYear(), currentMonday.getMonth(), currentMonday.getDate() + 6);
      weeks.push({ startDate: currentMonday, endDate: currentSunday });
      currentMonday = new Date(currentMonday.getFullYear(), currentMonday.getMonth(), currentMonday.getDate() + 7);
      if (currentMonday.getTime() > new Date(year, month + 1, 0).getTime()) {
        break;
      }
    }
    return weeks;
  }, []);

  const getInitialWeekIndex = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const weeks = getWeeksInMonth(year, month);
    const dTime = new Date(year, month, date.getDate()).getTime();
    
    for (let i = 0; i < weeks.length; i++) {
        const sTime = new Date(weeks[i].startDate.getFullYear(), weeks[i].startDate.getMonth(), weeks[i].startDate.getDate()).getTime();
        const eTime = new Date(weeks[i].endDate.getFullYear(), weeks[i].endDate.getMonth(), weeks[i].endDate.getDate()).getTime();
        if (dTime >= sTime && dTime <= eTime) {
            return i + 1;
        }
    }
    return weeks.length;
  }, [getWeeksInMonth]);

  const [currentMonth, setCurrentMonth] = useState(() => monthKey(today));
  const [weekIndex, setWeekIndex] = useState(() => getInitialWeekIndex(today));

  const getMonthInfo = useCallback((monthStr) => {
    const [y, m] = monthStr.split("-").map(Number);
    const year = y;
    const month = m - 1;
    const totalWeeks = getWeeksInMonth(year, month).length;
    return { year, month, totalWeeks };
  }, [getWeeksInMonth]);

  const computeWeekRange = useCallback((monthStr, idx) => {
    const { year, month } = getMonthInfo(monthStr);
    const weeks = getWeeksInMonth(year, month);
    const week = weeks[idx - 1] || weeks[weeks.length - 1]; // fallback para evitar crasheos si idx es mayor

    // Use Date.UTC to prevent timezone shift when using .toISOString() later
    const startDate = new Date(Date.UTC(week.startDate.getFullYear(), week.startDate.getMonth(), week.startDate.getDate(), 0, 0, 0, 0));
    const endDate = new Date(Date.UTC(week.endDate.getFullYear(), week.endDate.getMonth(), week.endDate.getDate(), 23, 59, 59, 999));
    return { startDate, endDate };
  }, [getMonthInfo, getWeeksInMonth]);

  const goPrevWeek = () => {
    const info = getMonthInfo(currentMonth);
    if (weekIndex > 1) {
      setWeekIndex((w) => w - 1);
      return;
    }
    // move to previous month last week
    const prevMonthDate = new Date(info.year, info.month - 1, 1);
    const prevKey = monthKey(prevMonthDate);
    const prevInfo = getMonthInfo(prevKey);
    setCurrentMonth(prevKey);
    setWeekIndex(prevInfo.totalWeeks);
  };

  const goNextWeek = () => {
    const info = getMonthInfo(currentMonth);
    if (weekIndex < info.totalWeeks) {
      setWeekIndex((w) => w + 1);
      return;
    }
    // move to next month first week
    const nextMonthDate = new Date(info.year, info.month + 1, 1);
    const nextKey = monthKey(nextMonthDate);
    setCurrentMonth(nextKey);
    setWeekIndex(1);
  };

  // --- View Payments Modal State ---
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);
  const [isReportFormatModalOpen, setIsReportFormatModalOpen] = useState(false);

  // --- Payment Form Modal State ---
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadInvoiceReference = useCallback(async () => {
    try {
      const nextRefRes = await getNextInvoiceReference();
      setNextInvoiceReference(nextRefRes.data?.data?.nextReference || null);
      setRecentInvoiceNumbers(nextRefRes.data?.data?.recentNumbers || []);
    } catch (refErr) {
      console.error("Error fetching next invoice reference:", refErr);
    }
  }, []);

  const fetchInvoices = useCallback(
    async (searchQuery, status, monthKeyParam, weekIdx, productId = "") => {
      setLoading(true);
      try {
        const params = {};
        if (!monthKeyParam || !weekIdx) {
          // require both month and week index to request weekly data
          setInvoices([]);
          setLoading(false);
          return [];
        }

        if (!!searchQuery === false) {
          // compute week start/end and send both to API
          const { startDate, endDate } = computeWeekRange(
            monthKeyParam,
            weekIdx,
          );
          params.weekStart = startDate.toISOString().slice(0, 10);
          params.weekEnd = endDate.toISOString().slice(0, 10);
        }

        if (searchQuery) params.search = searchQuery;
        if (status && status !== "all") params.status = status;
        if (productId) params.product = productId;

        const invoicesRes = await getInvoices(params);
        const fetchedInvoices = invoicesRes.data.data || [];
        setInvoices(fetchedInvoices);
        setProductSummary(invoicesRes.data.productSummary || []);
        return fetchedInvoices;
      } catch (error) {
        console.error("Error fetching invoices:", error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [computeWeekRange],
  );

  useEffect(() => {
    if (user) {
      fetchInvoices(
        debouncedSearchTerm,
        filterStatus,
        currentMonth,
        weekIndex,
        filterProduct,
      );
    }
  }, [
    user,
    debouncedSearchTerm,
    filterStatus,
    filterProduct,
    currentMonth,
    weekIndex,
    fetchInvoices,
  ]);

  useEffect(() => {
    if (user) {
      const fetchStaticData = async () => {
        setStaticDataLoading(true);
        try {
          const [clientsRes, productsRes, paymentMethodsRes, bcvRateRes] =
            await Promise.all([
              getClientList(),
              getProducts({ limit: 200 }),
              getPaymentMethods(),
              getBcvRate(),
            ]);
          setClients(clientsRes.data.data);
          setProducts(productsRes.data.data);
          setPaymentMethods(paymentMethodsRes.data.data);
          setBcvRate(bcvRateRes.data.data);
          try {
            await loadInvoiceReference();
          } catch (refErr) {
            console.error("Error fetching next invoice reference:", refErr);
          }
        } catch (error) {
          console.error("Error fetching static data:", error);
        } finally {
          setStaticDataLoading(false);
        }
      };
      fetchStaticData();
    }
  }, [user, loadInvoiceReference]);

  useEffect(() => {
    if (formInitialData) {
      setIsFormOpen(true);
      setEditingInvoice(null);
    }
  }, [formInitialData]);

  useEffect(() => {
    const clientId = location.state?.clientId;
    if (!clientId || staticDataLoading) return;
    setEditingInvoice(null);
    formSetInitialData({ clientId });
    setIsFormOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, staticDataLoading, formSetInitialData]);

  const handleOpenForm = (invoiceToEdit = null) => {
    setEditingInvoice(invoiceToEdit);
    setIsFormOpen(true);
    if (!invoiceToEdit) {
      loadInvoiceReference();
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingInvoice(null);
    formSetInitialData(null);
  };

  const handleSave = () => {
    // Si se guarda correctamente, limpiamos errores de generación de factura
    setNewInvoiceError("");
    handleCloseForm();
    fetchInvoices(
      debouncedSearchTerm,
      filterStatus,
      currentMonth,
      weekIndex,
      filterProduct,
    );
    loadInvoiceReference();
  };

  const handleNewInvoiceError = (message) => {
    const msg =
      message || "Ocurrió un error al generar la factura. Intente de nuevo.";
    setNewInvoiceError(msg);
    toast.error(msg, { position: "top-center" });
  };

  const handleInvoiceClick = (invoice) => {
    if (isFormOpen || isPaymentFormOpen) return;
    setSelectedInvoice(invoice);
    setIsPaymentsModalOpen(true);
  };

  const closePaymentsModal = () => {
    setIsPaymentsModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleEditClick = () => {
    if (!selectedInvoice) return;
    handleOpenForm(selectedInvoice);
    closePaymentsModal();
  };

  const handleOpenPaymentForm = () => {
    if (!selectedInvoice || !invoiceHasPendingBalance(selectedInvoice)) return;
    setEditingPayment(null);
    setIsPaymentFormOpen(true);
    setIsPaymentsModalOpen(false);
  };

  const handleOpenPaymentEditForm = (payment) => {
    if (!selectedInvoice) return;
    setEditingPayment(payment);
    setIsPaymentFormOpen(true);
    setIsPaymentsModalOpen(false);
  };

  const handleClosePaymentForm = () => {
    setIsPaymentFormOpen(false);
    setEditingPayment(null);
    setIsPaymentsModalOpen(true);
  };

  const handlePaymentSave = async () => {
    handleClosePaymentForm();
    const updatedInvoices = await fetchInvoices(
      debouncedSearchTerm,
      filterStatus,
      currentMonth,
      weekIndex,
      filterProduct,
    );
    const updatedInvoice = updatedInvoices.find(
      (inv) => inv._id === selectedInvoice?._id,
    );
    if (updatedInvoice) {
      setSelectedInvoice(updatedInvoice);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selectedInvoice) return;
    const confirmed = window.confirm("¿Desea eliminar este pago registrado?");
    if (!confirmed) return;

    try {
      await deleteInvoicePayment(selectedInvoice._id, paymentId);
      const updatedInvoices = await fetchInvoices(
        debouncedSearchTerm,
        filterStatus,
        currentMonth,
        weekIndex,
        filterProduct,
      );
      const updatedInvoice = updatedInvoices.find(
        (inv) => inv._id === selectedInvoice._id,
      );
      if (updatedInvoice) {
        setSelectedInvoice(updatedInvoice);
      } else {
        closePaymentsModal();
      }
      toast.success("Pago eliminado correctamente.", { position: "top-center" });
    } catch (error) {
      const message =
        error.response?.data?.error || "No se pudo eliminar el pago.";
      toast.error(message, { position: "top-center" });
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    const confirmed = window.confirm(
      `¿Desea eliminar la factura #${selectedInvoice.invoiceNumber}? Esta acción no se puede deshacer.`,
    );
    if (!confirmed) return;

    try {
      await deleteInvoice(selectedInvoice._id);
      closePaymentsModal();
      await fetchInvoices(
        debouncedSearchTerm,
        filterStatus,
        currentMonth,
        weekIndex,
        filterProduct,
      );
      toast.success("Factura eliminada correctamente.", {
        position: "top-center",
      });
    } catch (error) {
      const message =
        error.response?.data?.error || "No se pudo eliminar la factura.";
      toast.error(message, { position: "top-center" });
    }
  };

  const paymentMethodName = (payment) =>
    resolvePaymentMethodName(payment, paymentMethods);

  const buildWeeklyReportData = () => {
    const currentMonthLabel = new Date(currentMonth + "-01").toLocaleString(
      "es-VE",
      {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      },
    );
    const periodLabel = `Semana ${weekIndex} / ${getMonthInfo(currentMonth).totalWeeks} - ${currentMonthLabel}`;

    const productRows = productSummary.map((product) => ({
      product: product.name,
      quantity: product.totalQuantity,
      quesera: product.hasMaturation ? formatNumberVe(product.queseraKg) : "—",
      cava: product.hasMaturation ? formatNumberVe(product.cavaKg) : "—",
      kg: formatNumberVe(product.totalKg),
      remaining: formatRemaining(product),
      totalUSD: formatNumberVe(product.totalAmount),
      hasMaturation: Boolean(product.hasMaturation),
    }));

    const salesRows = invoices.map((invoice) => {
      const paid = (invoice.totalAmount || 0) - (invoice.pendingAmount || 0);
      return {
        invoiceNumber: invoice.invoiceNumber,
        client: invoice.client?.name || "Cliente no encontrado",
        date: formatDate(new Date(invoice.createdAt)),
        totalUSD: formatNumberVe(invoice.totalAmount || 0),
        totalPaidUSD: formatNumberVe(paid),
        totalPendingUSD: formatNumberVe(invoice.pendingAmount || 0),
        status: invoice.pendingAmount <= 0.01 ? "PAGADO" : "PENDIENTE",
      };
    });

    const totalPaidUSD = invoices.reduce(
      (acc, inv) => acc + ((inv.totalAmount || 0) - (inv.pendingAmount || 0)),
      0,
    );
    const totalPendingUSD = invoices.reduce(
      (acc, inv) => acc + (inv.pendingAmount || 0),
      0,
    );

    const paymentRows = [];
    invoices.forEach((invoice) => {
      const clientName = invoice.client?.name || "Cliente no encontrado";
      (invoice.payments || []).forEach((payment) => {
        const amountUSD =
          payment.currency === "USD"
            ? payment.amount
            : payment.conversionDetails?.amountInUSD || 0;
        paymentRows.push({
          client: clientName,
          invoiceNumber: invoice.invoiceNumber,
          paymentDate: payment.date
            ? formatDate(new Date(payment.date))
            : payment.createdAt
              ? formatDate(new Date(payment.createdAt))
              : "",
          method: paymentMethodName(payment),
          originalAmount: formatNumberVe(payment.amount || 0),
          currency: payment.currency || "",
          amountUSD: formatNumberVe(amountUSD),
          reference: payment.reference || "",
        });
      });
    });
    paymentRows.sort((a, b) => a.client.localeCompare(b.client, "es"));

    return {
      periodLabel,
      generatedAt: new Date().toLocaleString("es-VE"),
      productRows,
      salesRows,
      paymentRows,
      totalProductSummaryAmount: formatNumberVe(totalProductSummaryAmount),
      totalPaidUSD: formatNumberVe(totalPaidUSD),
      totalPendingUSD: formatNumberVe(totalPendingUSD),
    };
  };

  const handleDownloadWeeklyReportXlsx = async () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No hay datos de facturación en esta semana para exportar.", {
        position: "top-center",
      });
      return;
    }

    const data = buildWeeklyReportData();

    const workbook = new ExcelJS.Workbook();
    const styleHeader = {
      font: { bold: true, color: { argb: "FFFFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4F46E5" } },
      alignment: { vertical: "middle", horizontal: "center" },
      border: {
        top: { style: "thin", color: { argb: "D1D5DB" } },
        left: { style: "thin", color: { argb: "D1D5DB" } },
        bottom: { style: "thin", color: { argb: "D1D5DB" } },
        right: { style: "thin", color: { argb: "D1D5DB" } },
      },
    };
    const styleCell = {
      border: {
        top: { style: "thin", color: { argb: "E5E7EB" } },
        left: { style: "thin", color: { argb: "E5E7EB" } },
        bottom: { style: "thin", color: { argb: "E5E7EB" } },
        right: { style: "thin", color: { argb: "E5E7EB" } },
      },
      alignment: { vertical: "middle" },
    };

    const wsResumen = workbook.addWorksheet("Resumen");
    const resumenHeaders = hasMaturationSummary
      ? ["Producto", "Cantidad", "Quesera kg", "Cava kg", "Ventas kg", "Restante", "Total USD"]
      : ["Producto", "Cantidad", searchingInvoices ? "Ventas" : "Kg", "Total USD"];
    wsResumen.columns = hasMaturationSummary
      ? [{ width: 32 }, { width: 12 }, { width: 14 }, { width: 12 }, { width: 12 }, { width: 16 }, { width: 16 }]
      : [{ width: 36 }, { width: 14 }, { width: 12 }, { width: 16 }];
    wsResumen.addRow([COMPANY_NAME]);
    wsResumen.addRow([`RIF ${COMPANY_RIF}`]);
    wsResumen.addRow(["REPORTE SEMANAL DE FACTURACION"]);
    wsResumen.addRow([data.periodLabel]);
    wsResumen.addRow([`Generado: ${data.generatedAt}`]);
    wsResumen.addRow([]);
    wsResumen.addRow(["RESUMEN DE PRODUCTOS FACTURADOS"]);
    const resumenHeaderRow = wsResumen.addRow(resumenHeaders);
    resumenHeaderRow.eachCell((cell) => Object.assign(cell, { style: styleHeader }));
    data.productRows.forEach((row) => {
      const r = wsResumen.addRow(
        hasMaturationSummary
          ? [row.product, row.quantity, row.quesera, row.cava, row.kg, row.remaining, row.totalUSD]
          : [row.product, row.quantity, row.kg, row.totalUSD],
      );
      r.eachCell((cell) => Object.assign(cell, { style: styleCell }));
    });
    const totalRow = wsResumen.addRow(
      hasMaturationSummary
        ? ["TOTAL FINAL USD", "", "", "", "", "", data.totalProductSummaryAmount]
        : ["TOTAL FINAL USD", "", "", data.totalProductSummaryAmount],
    );
    totalRow.eachCell((cell) => {
      cell.style = {
        ...styleCell,
        font: { bold: true },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "EEF2FF" } },
      };
    });

    const wsVentas = workbook.addWorksheet("Ventas");
    wsVentas.columns = [
      { width: 12 },
      { width: 34 },
      { width: 24 },
      { width: 14 },
      { width: 17 },
      { width: 19 },
      { width: 12 },
    ];
    wsVentas.addRow([COMPANY_NAME]);
    wsVentas.addRow([`RIF ${COMPANY_RIF}`]);
    wsVentas.addRow(["VENTAS REGISTRADAS"]);
    const ventasHeaderRow = wsVentas.addRow([
      "Factura",
      "Cliente",
      "Fecha",
      "Total USD",
      "Total Pagado USD",
      "Total Pendiente USD",
      "Estado",
    ]);
    ventasHeaderRow.eachCell((cell) => Object.assign(cell, { style: styleHeader }));
    data.salesRows.forEach((row) => {
      const r = wsVentas.addRow([
        row.invoiceNumber,
        row.client,
        row.date,
        row.totalUSD,
        row.totalPaidUSD,
        row.totalPendingUSD,
        row.status,
      ]);
      r.eachCell((cell) => Object.assign(cell, { style: styleCell }));
    });
    const ventasTotalRow = wsVentas.addRow([
      "TOTALES",
      "",
      "",
      "",
      data.totalPaidUSD,
      data.totalPendingUSD,
      "",
    ]);
    ventasTotalRow.eachCell((cell) => {
      cell.style = {
        ...styleCell,
        font: { bold: true },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "EFF6FF" } },
      };
    });

    const wsPagos = workbook.addWorksheet("Pagos");
    wsPagos.columns = [
      { width: 28 },
      { width: 12 },
      { width: 24 },
      { width: 24 },
      { width: 16 },
      { width: 12 },
      { width: 14 },
      { width: 22 },
    ];
    wsPagos.addRow(["PAGOS REGISTRADOS (POR CLIENTE)"]);
    const pagosHeaderRow = wsPagos.addRow([
      "Cliente",
      "Factura",
      "Fecha Pago",
      "Metodo",
      "Monto Original",
      "Moneda",
      "Monto USD",
      "Referencia",
    ]);
    pagosHeaderRow.eachCell((cell) => Object.assign(cell, { style: styleHeader }));
    data.paymentRows.forEach((row) => {
      const r = wsPagos.addRow([
        row.client,
        row.invoiceNumber,
        row.paymentDate,
        row.method,
        row.originalAmount,
        row.currency,
        row.amountUSD,
        row.reference,
      ]);
      r.eachCell((cell) => Object.assign(cell, { style: styleCell }));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reporte_facturacion_semana_${weekIndex}_${currentMonth}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadWeeklyReportPdf = () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No hay datos de facturación en esta semana para exportar.", {
        position: "top-center",
      });
      return;
    }
    const data = buildWeeklyReportData();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    doc.setFontSize(11);
    doc.text(COMPANY_NAME, 40, 28);
    doc.setFontSize(9);
    doc.text(`RIF ${COMPANY_RIF}`, 40, 42);
    doc.setFontSize(14);
    doc.text("REPORTE SEMANAL DE FACTURACION", 40, 60);
    doc.setFontSize(10);
    doc.text(data.periodLabel, 40, 76);
    doc.text(`Generado: ${data.generatedAt}`, 40, 90);

    doc.setFontSize(11);
    doc.text("Resumen de productos facturados", 40, 112);
    autoTable(doc, {
      startY: 118,
      head: hasMaturationSummary
        ? [["Producto", "Cantidad", "Quesera kg", "Cava kg", "Ventas kg", "Restante", "Total USD"]]
        : [["Producto", "Cantidad", searchingInvoices ? "Ventas" : "Kg", "Total USD"]],
      body: data.productRows.map((row) =>
        hasMaturationSummary
          ? [row.product, row.quantity, row.quesera, row.cava, row.kg, row.remaining, row.totalUSD]
          : [row.product, row.quantity, row.kg, row.totalUSD],
      ),
      foot: [
        hasMaturationSummary
          ? ["TOTAL FINAL USD", "", "", "", "", "", data.totalProductSummaryAmount]
          : ["TOTAL FINAL USD", "", "", data.totalProductSummaryAmount],
      ],
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [79, 70, 229] },
      footStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42] },
    });

    let nextY = (doc.lastAutoTable?.finalY || 120) + 18;
    doc.setFontSize(11);
    doc.text("Ventas registradas", 40, nextY);
    autoTable(doc, {
      startY: nextY + 6,
      head: [[
        "Factura",
        "Cliente",
        "Fecha",
        "Total USD",
        "Total Pagado USD",
        "Total Pendiente USD",
        "Estado",
      ]],
      body: data.salesRows.map((row) => [
        row.invoiceNumber,
        row.client,
        row.date,
        row.totalUSD,
        row.totalPaidUSD,
        row.totalPendingUSD,
        row.status,
      ]),
      foot: [["TOTALES", "", "", "", data.totalPaidUSD, data.totalPendingUSD, ""]],
      theme: "striped",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] },
      footStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42] },
    });

    nextY = (doc.lastAutoTable?.finalY || nextY + 180) + 18;
    if (nextY > 500) {
      doc.addPage();
      nextY = 40;
    }
    doc.setFontSize(11);
    doc.text("Pagos registrados (por cliente)", 40, nextY);
    autoTable(doc, {
      startY: nextY + 6,
      head: [[
        "Cliente",
        "Factura",
        "Fecha Pago",
        "Metodo",
        "Monto Orig",
        "Moneda",
        "Monto USD",
        "Referencia",
      ]],
      body: data.paymentRows.map((row) => [
        row.client,
        row.invoiceNumber,
        row.paymentDate,
        row.method,
        row.originalAmount,
        row.currency,
        row.amountUSD,
        row.reference,
      ]),
      theme: "striped",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] },
    });

    doc.save(`reporte_facturacion_semana_${weekIndex}_${currentMonth}.pdf`);
  };

  const closeReportFormatModal = () => setIsReportFormatModalOpen(false);

  const handleSelectReportFormat = async (format) => {
    if (format === "excel") {
      await handleDownloadWeeklyReportXlsx();
      closeReportFormatModal();
      return;
    }

    if (format === "pdf") {
      handleDownloadWeeklyReportPdf();
      closeReportFormatModal();
    }
  };

  const handleDownloadWeeklyReport = async () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No hay datos de facturación en esta semana para exportar.", {
        position: "top-center",
      });
      return;
    }
    setIsReportFormatModalOpen(true);
  };

  if (isFormOpen) {
    return (
      <InvoiceForm
        clients={clients}
        products={products}
        paymentMethods={paymentMethods}
        bcvRate={bcvRate}
        nextInvoiceReference={nextInvoiceReference}
        recentInvoiceNumbers={recentInvoiceNumbers}
        editingInvoice={editingInvoice}
        initialData={formInitialData}
        setInitialData={formSetInitialData}
        onSave={handleSave}
        onCancel={handleCloseForm}
        onError={handleNewInvoiceError}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Facturación")}</title>
      </Helmet>
      <div className="app-page !space-y-0">
        {/* Header Section */}
        <div className="p-3 sm:p-6 border-b border-gray-200 bg-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4 sm:justify-between sm:items-center mb-2">
            <AppPageHeader
              title="Facturación"
              subtitle={
                isAdmin
                  ? "Control de facturas y ventas"
                  : "Facturas de tus clientes asignados"
              }
            />
            <div className="flex flex-col items-stretch w-full sm:w-auto">
              <div className="flex flex-wrap gap-2">
                {isAdmin && (
                  <Button
                    variant="secondary"
                    onClick={handleDownloadWeeklyReport}
                    disabled={staticDataLoading || loading || invoices.length === 0}
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline"> Descargar reporte semanal</span>
                  </Button>
                )}
                <Button
                  onClick={() => handleOpenForm()}
                  disabled={staticDataLoading}
                >
                  <Plus size={16} />
                  <span className="hidden sm:inline"> Nueva Factura</span>
                </Button>
              </div>
              {nextInvoiceReference && (
                <div className="mt-2 text-xs text-gray-600">
                  Referencia facturero actual:{" "}
                  <span className="font-semibold">{nextInvoiceReference}</span>
                </div>
              )}
              {newInvoiceError && (
                <div className="mt-2 text-xs sm:text-sm text-red-600 animate-[fadeInUp_0.25s_ease-out]">
                  {newInvoiceError}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Buscar por Nro de Factura o Cliente..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="sm:w-64">
                <label className="sr-only" htmlFor="invoice-product-filter">
                  Filtrar por producto
                </label>
                <select
                  id="invoice-product-filter"
                  className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                  disabled={staticDataLoading}
                >
                  <option value="">Todos los productos</option>
                  {sortedProducts.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-medium text-gray-500">Semana:</label>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-600 transition-colors"
                  onClick={goPrevWeek}
                  type="button"
                >
                  &lt;
                </button>
                <div className="px-3 py-1 border rounded">
                  <span className="text-sm font-medium">
                    Semana {weekIndex} / {getMonthInfo(currentMonth).totalWeeks}{" "}
                    -{" "}
                    {new Date(currentMonth + "-01").toLocaleString("es-VE", {
                      month: "long",
                      year: "numeric",
                      timeZone: "UTC",
                    })}
                  </span>
                </div>
                <button
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-600 transition-colors"
                  onClick={goNextWeek}
                  type="button"
                >
                  &gt;
                </button>
              </div>
              <div className="flex space-x-2 overflow-x-auto pb-2">
                <Button
                  variant={filterStatus === "all" ? "primary" : "secondary"}
                  onClick={() => setFilterStatus("all")}
                >
                  Todos
                </Button>
                <Button
                  variant={filterStatus === "pending" ? "primary" : "secondary"}
                  onClick={() => setFilterStatus("pending")}
                >
                  Pendientes
                </Button>
                <Button
                  variant={filterStatus === "paid" ? "primary" : "secondary"}
                  onClick={() => setFilterStatus("paid")}
                >
                  Pagados
                </Button>
              </div>
              <div className="flex items-center gap-1.5 sm:ml-auto">
                <span className="text-xs font-medium text-gray-500 hidden sm:inline">Vista</span>
                <Button
                  variant={viewMode === "grid" ? "primary" : "secondary"}
                  size="sm"
                  type="button"
                  title="Vista rejilla"
                  aria-label="Vista rejilla"
                  aria-pressed={viewMode === "grid"}
                  onClick={() => setInvoicesView("grid")}
                >
                  <LayoutGrid size={16} />
                </Button>
                <Button
                  variant={viewMode === "list" ? "primary" : "secondary"}
                  size="sm"
                  type="button"
                  title="Vista lista"
                  aria-label="Vista lista"
                  aria-pressed={viewMode === "list"}
                  onClick={() => setInvoicesView("list")}
                >
                  <List size={16} />
                </Button>
              </div>
            </div>
          </div>

          {/* Resumen de productos facturados en la vista actual */}
          {productSummary.length > 0 && (
            <div className="mt-6">
              <div
                className="flex justify-between items-center cursor-pointer mb-2"
                onClick={() => setIsSummaryVisible((v) => !v)}
              >
                <h3 className="text-sm font-semibold text-gray-700">
                  Resumen de productos facturados
                  {hasMaturationSummary ? (
                    <span className="ml-2 font-normal text-gray-500">
                      · quesera y cava del lote; ventas de esta semana
                    </span>
                  ) : null}
                </h3>
                <span className="text-sm font-medium text-blue-600 hover:text-blue-800">
                  {isSummaryVisible ? "Ocultar" : "Mostrar"}
                </span>
              </div>
              {isSummaryVisible && (
                <div className="max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 pr-4">Producto</th>
                        <th className="py-1 pr-4 text-right">Cantidad</th>
                        {hasMaturationSummary && (
                          <>
                            <th className="py-1 pr-4 text-right">Quesera</th>
                            <th className="py-1 pr-4 text-right">Cava</th>
                          </>
                        )}
                        <th className="py-1 pr-4 text-right">
                          {hasMaturationSummary || searchingInvoices ? "Ventas" : "Kg"}
                        </th>
                        {hasMaturationSummary && (
                          <th className="py-1 pr-4 text-right">Restante</th>
                        )}
                        <th className="py-1 text-right">Total $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productSummary.map((p) => (
                        <tr key={p.productId || p.name} className="border-b last:border-0 align-top">
                          <td className="py-1.5 pr-4">
                            <div>{p.name}</div>
                          </td>
                          <td className="py-1.5 pr-4 text-right">
                            {p.totalQuantity}
                          </td>
                          {hasMaturationSummary && (
                            <>
                              <td className="py-1.5 pr-4 text-right">
                                {p.hasMaturation ? `${formatNumberVe(p.queseraKg)} kg` : "—"}
                              </td>
                              <td className="py-1.5 pr-4 text-right">
                                {p.hasMaturation ? `${formatNumberVe(p.cavaKg)} kg` : "—"}
                              </td>
                            </>
                          )}
                          <td className="py-1.5 pr-4 text-right">
                            {formatNumberVe(p.totalKg)}
                            {hasMaturationSummary ? " kg" : ""}
                          </td>
                          {hasMaturationSummary && (
                            <td
                              className={`py-1.5 pr-4 text-right font-medium ${
                                p.hasMaturation && (p.remainingKg || 0) < 0
                                  ? "text-red-600"
                                  : "text-amber-800"
                              }`}
                            >
                              <span className="md:hidden">{formatRemainingUnit(p)}</span>
                              <span className="hidden md:inline">{formatRemaining(p)}</span>
                            </td>
                          )}
                          <td className="py-1.5 text-right">
                            {formatNumberVe(p.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 border-t-2 border-gray-300">
                        <td className="py-2 pr-4 font-semibold text-gray-700">
                          Total final $
                        </td>
                        <td />
                        {hasMaturationSummary && (
                          <>
                            <td />
                            <td />
                          </>
                        )}
                        <td />
                        {hasMaturationSummary && <td />}
                        <td className="py-2 text-right font-bold text-gray-900">
                          {formatNumberVe(totalProductSummaryAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content Section */}
        <div className="reveal-host p-3 sm:p-6 lg:flex-grow lg:overflow-y-auto">
          {loading || staticDataLoading ? (
            <LoadingState
              variant={viewMode === "grid" ? "cards" : "list"}
              count={8}
              label="Cargando facturas"
            />
          ) : invoices.length > 0 ? (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {invoices.map((inv) => (
                  <Card
                    key={inv._id}
                    className={`p-0 overflow-hidden transition-all ${staticDataLoading ? "cursor-not-allowed opacity-50" : "hover:shadow-md cursor-pointer"}`}
                    onClick={
                      staticDataLoading
                        ? undefined
                        : () => handleInvoiceClick(inv)
                    }
                  >
                    <div className="p-4 border-b border-gray-100 flex justify-between items-start">
                      <div>
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          # {inv.invoiceNumber}
                        </span>
                        <h3 className="font-bold text-gray-900 mt-2">
                          {inv.client?.name || "Cliente no encontrado"}
                        </h3>
                        {sellerLabel(inv.client?.seller) ? (
                          <span className="inline-flex mt-1.5 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                            {sellerLabel(inv.client?.seller)}
                          </span>
                        ) : null}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(new Date(inv.createdAt))}
                        </p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded-full ${inv.pendingAmount <= 0.01 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                        >
                          {inv.pendingAmount <= 0.01 ? "PAGADO" : "PENDIENTE"}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50/50">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Total</span>
                        <span className="font-bold text-gray-900">
                          {formatCurrency(inv.totalAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Pendiente</span>
                        <span className="font-bold text-red-500">
                          {formatCurrency(inv.pendingAmount)}
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <AppTable
                columns={[
                  { key: "number", title: "Factura" },
                  { key: "client", title: "Cliente" },
                  { key: "seller", title: "Vendedor" },
                  { key: "date", title: "Fecha" },
                  { key: "total", title: "Total", align: "right" },
                  { key: "pending", title: "Pendiente", align: "right" },
                  { key: "status", title: "Estado", align: "center" },
                ]}
              >
                {invoices.map((inv) => {
                  const paid = inv.pendingAmount <= 0.01;
                  return (
                    <tr
                      key={inv._id}
                      className={`hover:bg-gray-50 transition-colors ${staticDataLoading ? "opacity-50" : "cursor-pointer"}`}
                      onClick={
                        staticDataLoading ? undefined : () => handleInvoiceClick(inv)
                      }
                    >
                      <td className="px-6 py-3.5 text-sm font-semibold text-blue-700 whitespace-nowrap">
                        #{inv.invoiceNumber}
                      </td>
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {inv.client?.name || "Cliente no encontrado"}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {sellerLabel(inv.client?.seller) || "—"}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(new Date(inv.createdAt))}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right font-medium text-gray-900">
                        {formatCurrency(inv.totalAmount)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right font-medium text-red-500">
                        {formatCurrency(inv.pendingAmount)}
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap ${paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                        >
                          {paid ? "PAGADO" : "PENDIENTE"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </AppTable>
            )
          ) : (
            <div className="text-center py-10 text-gray-500">
              No se encontraron facturas que coincidan con su búsqueda.
            </div>
          )}
        </div>
      </div>

      <AppModalShell
        open={isReportFormatModalOpen}
        onClose={closeReportFormatModal}
        title="Descargar reporte semanal"
        widthClass="max-w-md"
        footer={
          <AppModalFooter>
            <span />
            <Button variant="ghost" className="w-full sm:w-auto" onClick={closeReportFormatModal}>
              Cancelar
            </Button>
          </AppModalFooter>
        }
      >
        <p className="text-sm text-gray-500 mb-4">Selecciona el formato del reporte.</p>
        <div className="space-y-2">
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => handleSelectReportFormat("excel")}
          >
            Descargar en Excel
          </Button>
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => handleSelectReportFormat("pdf")}
          >
            Descargar en PDF
          </Button>
        </div>
      </AppModalShell>

      <InvoiceViewModal
        open={isPaymentsModalOpen && Boolean(selectedInvoice)}
        onClose={closePaymentsModal}
        invoice={selectedInvoice}
        titlePrefix="Pagos de Factura"
        mode="manage"
        creditLogs={selectedInvoice?.client?.creditLogs}
        paymentMethods={paymentMethods}
        onEditInvoice={canManageInvoices ? handleEditClick : undefined}
        onDeleteInvoice={canManageInvoices ? handleDeleteInvoice : undefined}
        onRegisterPayment={handleOpenPaymentForm}
        onEditPayment={canManagePayments ? handleOpenPaymentEditForm : undefined}
        onDeletePayment={canManagePayments ? handleDeletePayment : undefined}
      />

      {isPaymentFormOpen && selectedInvoice && (
        <PaymentForm
          invoice={selectedInvoice}
          paymentMethods={paymentMethods}
          bcvRate={bcvRate}
          editingPayment={editingPayment}
          onSave={handlePaymentSave}
          onCancel={handleClosePaymentForm}
        />
      )}
    </>
  );
};

export default InvoicesModule;
